import React, { useState, useEffect, useRef } from 'react';
import Header from './common/Header'
import Footer from './common/Footer'
import SEO from '../components/common/SEO';
import './HotelsListing.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import LoginPopup from './popup/LoginPopup';
import ReviewModal from './popup/ReviewModal';
import axios from 'axios';
import { toast } from "react-toastify";
import { getValidSession } from "./helpers/sessionUtils";
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { buildVendorProfileUrl } from '../utils/vendorProfileUrl';
import ListingVerifiedBadge from '../components/common/ListingVerifiedBadge';
import VendorProfileButton from '../components/common/VendorProfileButton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];
const vendorProfileUrl = (item) => buildVendorProfileUrl(item?.Vendor);

// ─── STAR RATING COMPONENT ────────────────────────────────────────────────────
const StarRating = ({ avg = 0, count = 0, onClick }) => {
    const fullStars = Math.floor(avg);
    const hasHalf = avg - fullStars >= 0.5;

    return (
        <div
            className="d-flex align-items-center justify-content-center gap-2"
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
        >
            <div className="d-flex" style={{ gap: '2px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <i
                        key={star}
                        className={`bi ${star <= fullStars
                            ? 'bi-star-fill'
                            : star === fullStars + 1 && hasHalf
                                ? 'bi-star-half'
                                : 'bi-star'
                            } text-warning`}
                        style={{ fontSize: '16px' }}
                    />
                ))}
            </div>
            <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>
                {Number(avg || 0).toFixed(1)}/5
            </span>
            <span className="text-muted" style={{ fontSize: '12px' }}>
                ({count} reviews)
            </span>
        </div>
    );
};

// ─── AMENITY ICON HELPER ──────────────────────────────────────────────────────
const ADMIN_AMENITY_ICONS = {
    'wifi': 'ri-wifi-line',
    'parking': 'ri-car-line',
    'swimming pool': 'ri-water-flash-line',
    'restaurant': 'ri-restaurant-2-line',
    'pet friendly': 'ri-baidu-line',
    'gym/fitness center': 'ri-run-line',
    'air conditioning': 'ri-windy-line',
    'room service': 'ri-customer-service-2-line',
    'telephone': 'ri-phone-line',
    'geyser / hot water': 'ri-shower-line',
    'spa': 'ri-plant-line',
    'bar/lounge': 'ri-goblet-line',
};

const getAmenityIcon = (amenity) => {
    const a = String(amenity || '').trim().toLowerCase();
    if (ADMIN_AMENITY_ICONS[a]) return ADMIN_AMENITY_ICONS[a];
    if (a.includes('wifi') || a.includes('internet')) return 'ri-wifi-line';
    if (a.includes('parking')) return 'ri-car-line';
    if (a.includes('pool') || a.includes('swimming')) return 'ri-water-flash-line';
    if (a.includes('restaurant') || a.includes('breakfast') || a.includes('meal') || a.includes('dining')) return 'ri-restaurant-2-line';
    if (a.includes('pet')) return 'ri-baidu-line';
    if (a.includes('gym') || a.includes('fitness')) return 'ri-run-line';
    if (a.includes('ac') || a.includes('air conditioning') || a.includes('cooling')) return 'ri-windy-line';
    if (a.includes('room service')) return 'ri-customer-service-2-line';
    if (a.includes('phone') || a.includes('telephone') || a.includes('contact')) return 'ri-phone-line';
    if (a.includes('geyser') || a.includes('hot water') || a.includes('shower')) return 'ri-shower-line';
    if (a.includes('spa') || a.includes('massage')) return 'ri-plant-line';
    if (a.includes('bar') || a.includes('lounge') || a.includes('drink')) return 'ri-goblet-line';
    return 'ri-checkbox-circle-line';
};

// ─── HOTEL CARD COMPONENT ─────────────────────────────────────────────────────
const HotelCard = ({ pkg, onAction, onCardClick, onReviewClick, isSubmitting, pendingAction, activeId }) => {
    const isActive = activeId === pkg.id && isSubmitting;

    const hotelImages = Array.isArray(pkg.Images) ? pkg.Images : [];
    const preferredImage =
        hotelImages.find((image) => image.image_size_name === 'large') ||
        hotelImages.reduce((best, image) => {
            const sizePriority = { large: 4, card: 3, profile: 2, thumbnail: 1 };
            return (sizePriority[image.image_size_name] || 0) > (sizePriority[best?.image_size_name] || 0)
                ? image
                : best;
        }, null) ||
        hotelImages[0];
    const displayImage = preferredImage?.image_url ||
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=95';

    let amenities = [];
    if (typeof pkg.amenities === 'string') {
        try { amenities = JSON.parse(pkg.amenities); } catch (e) { /* noop */ }
    } else if (Array.isArray(pkg.amenities)) {
        amenities = pkg.amenities;
    }
    const visibleAmenities = amenities.slice(0, 5);
    const hiddenAmenitiesCount = Math.max(amenities.length - visibleAmenities.length, 0);

    return (
        <div
            className="card mb-2 shadow-sm rounded-2 bg-white overflow-hidden hotel-result-card"
            style={{ border: '1px solid #e0e0e0', cursor: 'default' }}
        >
            <div className="row g-0 align-items-stretch">

                {/* ── IMAGE ─────────────────────────────────────── */}
                <div className="col-12 col-md-4 col-lg-3 p-0 d-flex align-items-stretch justify-content-stretch bg-light-subtle">
                    <div className="overflow-hidden w-100 position-relative" style={{ minHeight: 220 }}>
                        <img
                            src={displayImage}
                            alt={pkg.hotel_name}
                            className="w-100 h-100"
                            style={{
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer',
                                filter: 'none',
                                opacity: 1
                            }}
                            onClick={() => onCardClick(pkg)}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                    'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=95';
                            }}
                        />
                        <ListingVerifiedBadge item={pkg} />
                        {/* VERIFIED BADGE - TOP LEFT */}
                        {/* Price badge top-right */}
                        <div className="position-absolute badge bg-primary price hotel-listing-price-badge ">
                            ₹{pkg.offer_price || pkg.hotel_price}
                            <span style={{ fontWeight: 400, fontSize: 11 }}>/night</span>
                        </div>
                    </div>
                </div>

                {/* ── INFO ──────────────────────────────────────── */}
                <div
                    className="col-12 col-md-8 col-lg-6 p-3 text-start"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick(pkg)}
                >
                    {/* Title */}
                    <h4
                        className=""
                        style={{ color: '#333', fontSize: 18 }}
                    >
                        {pkg.hotel_name}
                    </h4>

                    {/* Location badge */}
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <span className="badge bg-light text-dark" style={{ fontSize: 12 }}>
                            <i className="bi bi-geo-alt-fill me-1" style={{ color: '#dc3545' }}></i>
                            {pkg.city}, {pkg.state}
                        </span>
                        {vendorProfileUrl(pkg) && (
                            <VendorProfileButton to={vendorProfileUrl(pkg)} />
                        )}
                    </div>

                    {/* Description */}
                    <p className="mb-3 hotel-card-description" style={{ fontSize: 14, lineHeight: '1.5' }}>
                        {pkg.description?.length > 120 ? `${pkg.description.substring(0, 120)}...` : pkg.description}
                    </p>

                    {/* Amenities */}
                    <div className="d-flex flex-wrap gap-3 hotel-amenities-list">
                        {amenities.length > 0 ? (
                            <>
                            {visibleAmenities.map((amenity, idx) => (
                                <span
                                    key={idx}
                                    className="badge hotel-amenity-chip"
                                >
                                    <i className={`${getAmenityIcon(amenity)} me-2`}></i>
                                    {amenity}
                                </span>
                            ))}
                            {hiddenAmenitiesCount > 0 && (
                                <span
                                    className="badge hotel-amenity-chip hotel-amenity-more"
                                >
                                    <i className="ri-add-circle-line me-2"></i>
                                    +{hiddenAmenitiesCount} more amenities
                                </span>
                            )}
                            </>
                        ) : (
                            <span style={{ color: '#4b5563', fontSize: 13 }}>No amenities listed</span>
                        )}
                    </div>
                </div>

                {/* ── BUTTONS ───────────────────────────────────── */}
                <div className="col-12 col-lg-3 p-3 p-lg-4 d-flex flex-column gap-2 border-start hotel-card-actions">
                    {/* Star Rating */}
                    <div className="d-none d-lg-block mb-lg-3 pb-lg-3 border-bottom">
                        <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                    </div>

                    <div className="d-none d-md-block d-lg-none mb-2 pb-2 border-bottom text-center">
                        <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                    </div>

                    {/* Mobile: review summary left, call/WhatsApp icons right */}
                    <div className="hotel-mobile-action-row d-md-none">
                        <div className="hotel-mobile-rating">
                            <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                        </div>
                        <div className="hotel-mobile-icons">
                            <button
                                className="hotel-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'call', pkg)}
                                disabled={isSubmitting}
                                title="Call Now"
                            >
                                {isActive && pendingAction === 'call' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                                )}
                            </button>
                            <button
                                className="hotel-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'whatsapp', pkg)}
                                disabled={isSubmitting}
                                title="WhatsApp Now"
                            >
                                {isActive && pendingAction === 'whatsapp' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Tablet: compact icon-only buttons in a row */}
                    <div className="d-none d-md-flex d-lg-none gap-2 justify-content-center">
                        <button
                            className="btn btn-outline-primary fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'call', pkg)}
                            disabled={isSubmitting}
                            title="Call Now"
                        >
                            {isActive && pendingAction === 'call' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-telephone-fill"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-success fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'whatsapp', pkg)}
                            disabled={isSubmitting}
                            title="WhatsApp Now"
                        >
                            {isActive && pendingAction === 'whatsapp' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-whatsapp"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-dark fw-bold rounded-3 d-flex align-items-center justify-content-center listing-book-btn"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'book', pkg)}
                            disabled={isSubmitting}
                            title="Book Now"
                        >
                            {isActive && pendingAction === 'book' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-calendar-check-fill"></i>
                            )}
                        </button>
                    </div>

                    {/* Desktop: stacked full-width buttons */}
                    <div className="d-none d-lg-flex flex-column gap-2">
                        {/* Call Now */}
                        <button
                            className="btn btn-outline-primary fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'call', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'call' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-telephone-fill me-2"></i>
                            )}
                            {isActive && pendingAction === 'call' ? 'Connecting...' : 'Call Now'}
                        </button>

                        {/* WhatsApp */}
                        <button
                            className="btn btn-outline-success fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'whatsapp', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'whatsapp' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-whatsapp me-2"></i>
                            )}
                            {isActive && pendingAction === 'whatsapp' ? 'Connecting...' : 'WhatsApp Now'}
                        </button>

                        {/* Book Now */}
                        <button
                            className="btn btn-outline-dark fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100 listing-book-btn"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'book', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'book' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : null}
                            {isActive && pendingAction === 'book' ? 'Processing...' : 'Book Now'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── PAGINATION COMPONENT ─────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else if (currentPage <= 3) {
            for (let i = 1; i <= maxVisible; i++) pages.push(i);
            pages.push('...'); pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
            pages.push(1); pages.push('...');
            for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1); pages.push('...');
            for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
            pages.push('...'); pages.push(totalPages);
        }
        return pages;
    };

    return (
        <nav aria-label="Page navigation" style={{ marginTop: 30, marginBottom: 30 }}>
            <ul className="pagination d-flex justify-content-center flex-wrap gap-2" style={{ listStyle: 'none', padding: 0 }}>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: '#32AEE4' }}
                    >
                        <i className="bi bi-chevron-left"></i> Previous
                    </button>
                </li>

                {getPageNumbers().map((page, idx) => (
                    <li key={idx} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => typeof page === 'number' && onPageChange(page)}
                            disabled={page === '...'}
                            style={{
                                padding: '8px 12px',
                                border: page === currentPage ? '2px solid #32AEE4' : '1px solid #ddd',
                                borderRadius: 4,
                                backgroundColor: page === currentPage ? '#32AEE4' : 'white',
                                color: page === currentPage ? 'white' : '#32AEE4',
                                cursor: page === '...' ? 'default' : 'pointer',
                                fontWeight: page === currentPage ? 600 : 400,
                                minWidth: 36
                            }}
                        >
                            {page}
                        </button>
                    </li>
                ))}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 4, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: '#32AEE4' }}
                    >
                        Next <i className="bi bi-chevron-right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
function HotelsListing() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const cityFromUrl = queryParams.get('city') || '';
    const hotelFromUrl = queryParams.get('hotel') || '';

    const hotelsPerPage = 20;

    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(cityFromUrl);
    const [locationInput, setLocationInput] = useState(cityFromUrl);
    const [searchHotel, setSearchHotel] = useState(hotelFromUrl);
    const [appliedLocation, setAppliedLocation] = useState(cityFromUrl);
    const [appliedHotel, setAppliedHotel] = useState(hotelFromUrl);
    const [packages, setPackages] = useState([]);
    const [visiblePackages, setVisiblePackages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const [showThanksModal, setShowThanksModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeEntity, setActiveEntity] = useState(null);
    const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);

    // ── REVIEW STATE ───────────────────────────────────────────────────────────
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [reviewIntent, setReviewIntent] = useState(false); // true = login was triggered by review click
    const loginSuccessRef = useRef(false);
    const locationAutoRef = useRef(null);
    const hotelAutoRef = useRef(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAP_KEY,
        libraries,
    });

    const [user, setUser] = useState(() => {
        const session = getValidSession();
        return session?.user?.user || session?.user || null;
    });

    useEffect(() => {
        const session = getValidSession();
        setUser(session?.user?.user || session?.user || null);
    }, [isLoginPopupOpen]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const city = params.get('city') || '';
        const hotel = params.get('hotel') || '';
        setSelectedLocation(city);
        setLocationInput(city);
        setSearchHotel(hotel);
        setAppliedLocation(city);
        setAppliedHotel(hotel);
    }, [location.search]);

    const getCityFromPlace = (place) => {
        if (!place) return '';
        const components = place.address_components || [];
        const findType = (type) => {
            const component = components.find((item) => item.types && item.types.includes(type));
            return component ? component.long_name : null;
        };
        return findType('locality') || findType('administrative_area_level_2') || place.name || '';
    };

    const handleLocationPlaceChanged = () => {
        const instance = locationAutoRef.current;
        if (!instance) return;
        const place = instance.getPlace();
        const city = getCityFromPlace(place);
        if (city) {
            setSelectedLocation(city);
            setLocationInput(city);
        }
    };

    const getHotelNameFromPlace = (place) => {
        if (!place) return '';
        return place.name || place.formatted_address || '';
    };

    const handleHotelPlaceChanged = () => {
        const instance = hotelAutoRef.current;
        if (!instance) return;
        const place = instance.getPlace();
        const hotelName = getHotelNameFromPlace(place);
        if (hotelName) {
            setSearchHotel(hotelName);
        }
    };

    // Fetch cities
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/hotels/cities`);
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    setLocations(response.data.data);
                }
            } catch (error) {
                console.error('Failed to fetch hotel locations:', error);
            }
        };
        fetchLocations();
    }, []);

    // Fetch hotels
    useEffect(() => {
        const fetchHotels = async () => {
            setIsLoading(true);
            try {
                let url = `${API_BASE_URL}/hotels/hotel-list`;
                const params = [];
                if (appliedLocation) params.push(`city=${encodeURIComponent(appliedLocation)}`);
                if (appliedHotel) params.push(`hotel=${encodeURIComponent(appliedHotel)}`);
                if (params.length > 0) url += `?${params.join('&')}`;

                const response = await axios.get(url);
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    const data = response.data.data;
                    setPackages(data);
                    setVisiblePackages(data.slice(0, hotelsPerPage));
                    setCurrentPage(1);
                } else {
                    setPackages([]);
                    setVisiblePackages([]);
                }
            } catch (error) {
                console.error('Failed to fetch hotels:', error);
                setPackages([]);
                setVisiblePackages([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchHotels();
    }, [appliedLocation, appliedHotel]);

    const handlePageChange = (pageNumber) => {
        const startIndex = (pageNumber - 1) * hotelsPerPage;
        const endIndex = startIndex + hotelsPerPage;
        setVisiblePackages(packages.slice(startIndex, endIndex));
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const slugify = (str) =>
        str
            ? String(str)
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
                .replace(/^-+/, '')
                .replace(/-+$/, '')
            : '';

    // Expected /hotel-details/:slug format in HotelDetails.js:
    // <hotelname>-<city>-<price>-<id>
    const generateHotelSlug = (hotel) => {
        const name = slugify(hotel?.hotel_name || hotel?.name);
        const city = slugify(hotel?.city || hotel?.location_city);
        const price = Math.round(hotel?.offer_price || hotel?.hotel_price || hotel?.price || 0);
        const id = hotel?.id;

        return [name, city, price ? price : null, id]
            .filter(Boolean)
            .join('-')
            .replace(/--+/g, '-');
    };

    // Redirect using the slug-based detail route
    const handleCardClick = (pkg) => {
        const slug = generateHotelSlug(pkg);
        window.open(`/hotel-details/${slug}`, '_blank', 'noopener,noreferrer');
    };



    // ── LEAD FLOW ──────────────────────────────────────────────────────────────
    const handleAction = (e, type, pkg) => {
        e.preventDefault();
        e.stopPropagation();
        if (isSubmitting) return;

        setReviewIntent(false); // not a review action
        setActiveEntity(pkg);
        setPendingAction(type);

        if (!user) {
            setIsLoginPopupOpen(true);
            return;
        }
        processLead(type, user, pkg);
    };

    const handleLoginSuccess = (loggedInUser) => {
        loginSuccessRef.current = true;
        const session = getValidSession();
        const resolvedUser = session?.user?.user || session?.user || loggedInUser;
        setUser(resolvedUser);
        setIsLoginPopupOpen(false);

        if (reviewIntent && selectedHotel) {
            setReviewIntent(false);
            setShowReviewModal(true);
        } else if (pendingAction && activeEntity) {
            processLead(pendingAction, resolvedUser, activeEntity);
        }
    };

    const handleLoginPopupClose = () => {
        setIsLoginPopupOpen(false);
        setIsSubmitting(false);
        setTimeout(() => {
            if (!loginSuccessRef.current && pendingAction && activeEntity) {
                setPendingAction(null);
                setActiveEntity(null);
            }
            loginSuccessRef.current = false;
        }, 0);
    };

    // ── REVIEW HANDLERS ────────────────────────────────────────────────────────
    const handleReviewClick = (hotel) => {
        setSelectedHotel(hotel);
        if (!user) {
            setReviewIntent(true);
            setIsLoginPopupOpen(true);
            return;
        }
        setShowReviewModal(true);
    };

    const processLead = async (actionType, userData, pkg) => {
        setIsSubmitting(true);
        const currentUser = userData || user;

        const payload = {
            name: currentUser?.name || 'Guest',
            email: currentUser?.email || '',
            mobile: (currentUser?.phone || currentUser?.mobile || '').replace(/^(\+91|91)/, ''),
            type: 'hotel',
            type_id: pkg.id,
            message: `User clicked ${actionType} for ${pkg.hotel_name}`
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/enquiries/create`, payload);
            if (res.data.success) {
                setShowThanksModal(true);
            } else {
                toast.error(res.data?.message || 'Could not process request. Please try again.');
                setPendingAction(null);
            }
        } catch (err) {
            toast.error('Error processing request. Please try again.');
            console.error(err);
            setIsSubmitting(false);
            setPendingAction(null);
            setActiveEntity(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleThankYouClose = () => {
        setShowThanksModal(false);
        setPendingAction(null);
        setActiveEntity(null);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const params = [];
        const resolvedLocation = selectedLocation || locationInput;
        if (resolvedLocation) params.push(`city=${encodeURIComponent(resolvedLocation)}`);
        if (searchHotel) params.push(`hotel=${encodeURIComponent(searchHotel)}`);
        const query = params.length > 0 ? `?${params.join('&')}` : '';
        navigate(`/hotel-list${query}`);
        setAppliedLocation(resolvedLocation);
        setAppliedHotel(searchHotel);
    };

    const totalPages = Math.ceil(packages.length / hotelsPerPage);

    return (
        <div>
            <SEO
                title={appliedLocation ? `Hotels in ${appliedLocation} | Mangoloops` : 'Luxury Hotels & Stays | Mangoloops'}
                description="Find and book the best luxury hotels and stays at Mangoloops. Explore top-rated accommodation with premium amenities."
            />
            <Header />



            {/* ── SEARCH BAR ──────────────────────────────────── */}
            <div className="banner-content tour-search-sec text-center mb-5 hotel-listing-filter-section">
                <div className="container-fluid container-package">
                    <div className="row justify-content-center py-xl-4">
                        <div className="card border-0 border-radius-12 tab-shadow hotel-listing-filter-card">
                            <div className="card-body padding-14">
                                <form className="filter-form hotel-listing-filter-form p-0" onSubmit={handleSearchSubmit}>
                                    <div className="form-group hotel-filter-field">
                                        {isLoaded && !loadError ? (
                                            <Autocomplete
                                                onLoad={(autocomplete) => { locationAutoRef.current = autocomplete; }}
                                                onPlaceChanged={handleLocationPlaceChanged}
                                                options={{ types: ["(cities)"] }}
                                            >
                                                <input
                                                    type="text"
                                                    id="location-select"
                                                    name="location"
                                                    className={locationInput ? 'filter-value-active' : ''}
                                                    placeholder="Select Location or City"
                                                    value={locationInput}
                                                    onChange={(e) => {
                                                        setLocationInput(e.target.value);
                                                        setSelectedLocation(e.target.value);
                                                    }}
                                                />
                                            </Autocomplete>
                                        ) : (
                                            <input
                                                type="text"
                                                id="location-select"
                                                name="location"
                                                className={locationInput ? 'filter-value-active' : ''}
                                                list="hotel-location-options"
                                                placeholder="Select Location or City"
                                                value={locationInput}
                                                onChange={(e) => {
                                                    setLocationInput(e.target.value);
                                                    setSelectedLocation(e.target.value);
                                                }}
                                            />
                                        )}
                                        <datalist id="hotel-location-options">
                                            {locations.map((loc, index) => (
                                                <option key={index} value={loc} />
                                            ))}
                                        </datalist>
                                    </div>

                                    <div className="form-group form-border-left hotel-filter-field">
                                        {isLoaded && !loadError ? (
                                            <Autocomplete
                                                onLoad={(autocomplete) => {
                                                    hotelAutoRef.current = autocomplete;
                                                    try {
                                                        autocomplete.setFields(["name", "formatted_address", "place_id", "types"]);
                                                    } catch (e) { }
                                                }}
                                                onPlaceChanged={handleHotelPlaceChanged}
                                                options={{
                                                    types: ["lodging"],
                                                    componentRestrictions: { country: "in" },
                                                    fields: ["name", "formatted_address", "place_id", "types"]
                                                }}
                                            >
                                                <input
                                                    type="text"
                                                    id="checkin-out"
                                                    className={searchHotel ? 'filter-value-active' : ''}
                                                    placeholder="Search Hotel name Eg. Taj Hotel"
                                                    value={searchHotel}
                                                    onChange={(e) => setSearchHotel(e.target.value)}
                                                />
                                            </Autocomplete>
                                        ) : (
                                            <input
                                                type="text"
                                                id="checkin-out"
                                                className={searchHotel ? 'filter-value-active' : ''}
                                                placeholder="Search Hotel name Eg. Taj Hotel"
                                                value={searchHotel}
                                                onChange={(e) => setSearchHotel(e.target.value)}
                                            />
                                        )}
                                    </div>

                                    <button type="submit" className="submit-btn-filter">Search</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── RESULTS ─────────────────────────────────────── */}
            <section className="mt-5 mb-7">
                <div className="container">
                    {/* Result count header */}
                    <div className="row mb-3">
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
                            <h3 className="tour-title-of-trip holidays-left mb-0 heading-div">
                                {appliedLocation ? `Hotels in ${appliedLocation}` : 'All Hotels'}
                            </h3>
                            {!isLoading && (
                                <div className="text-muted" style={{ fontSize: 14 }}>
                                    Showing {visiblePackages.length} of {packages.length} results
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row mt-2">
                        {/* Loading spinner */}
                        {isLoading && (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!isLoading && visiblePackages.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-info text-center">
                                    No hotels found for the selected filters.
                                </div>
                            </div>
                        )}

                        {/* Cards */}
                        {!isLoading && visiblePackages.map((pkg) => (
                            <div className="col-12 mb-2" key={pkg.id}>
                                <HotelCard
                                    pkg={pkg}
                                    onAction={handleAction}
                                    onCardClick={handleCardClick}
                                    onReviewClick={handleReviewClick}
                                    isSubmitting={isSubmitting}
                                    pendingAction={pendingAction}
                                    activeId={activeEntity?.id}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {packages.length > hotelsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </section>

            <Footer />

            {/* ── LOGIN POPUP ─────────────────────────────────── */}
            {isLoginPopupOpen && (
                <div
                    className="custom-modal-overlay"
                    onClick={() => {
                        setIsLoginPopupOpen(false);
                        setIsSubmitting(false);
                        setPendingAction(null);
                        setActiveEntity(null);
                    }}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <LoginPopup
                            visible={isLoginPopupOpen}
                            onClose={handleLoginPopupClose}
                            onLoginSuccess={handleLoginSuccess}
                            serviceType="Hotel"
                        />
                    </div>
                </div>
            )}

            {/* ── THANK YOU MODAL ─────────────────────────────── */}
            {(isSubmitting || showThanksModal) && (
                <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
                    <div
                        className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: 400, background: 'white' }}
                    >
                        {isSubmitting && !showThanksModal ? (
                            <>
                                <div className="mb-4">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Processing your request...</h4>
                                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
                            </>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h2 className="fw-bold mb-3">Thank You!</h2>
                                <p className="text-muted mb-4">
                                    Your request has been sent to the team. We will get back to you shortly.
                                </p>
                                <button
                                    className="btn btn-primary rounded-pill px-4 py-2 fw-bold"
                                    onClick={handleThankYouClose}
                                >
                                    OK
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── REVIEW MODAL ────────────────────────────────── */}
            {showReviewModal && selectedHotel && (
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedHotel(null);
                    }}
                    entity={selectedHotel}
                    entityType="hotel"
                    user={user}
                    apiEndpoint="/hotels"
                />
            )}
        </div>
    );
}

export default HotelsListing;
